import React, { useMemo, useState } from 'react';
import Select from '../../shared/Select';
import { ReactComponent as CombinedLayoutIcon } from '../../assets/icons/combined-layout.svg';
import { ReactComponent as SplitLayoutIcon } from '../../assets/icons/split-layout.svg';
import { DESIGN_MODES } from './Step2Container';
import '../../styles/mockup-generator/DesignModeSelector.scss';
import { components } from 'react-select'
import { useSelectedVariantContext } from './context/SelectedVariantContext';
import {actionChangeDesignMode, actionChangeTabDesign, updateActiveSide} from './context/SelectedVariantContext/action'
import {mergeAttributesByDesignEdited} from './context/SelectedVariantContext/helper'
import {usePreviewArtworks} from '../../hooks/usePreviewArtworks';
import { Tooltip } from 'reactstrap';
import { useEditorSides } from '../../hooks/useEditorSides';
import classNames from 'classnames';

const CustomOption = (props) => {
    const { label, icon, description } = props.data;

    return (
        <components.Option {...props} className={`design-mode-option ${props.isSelected ? 'selected' : ''}`}>
            <div className='design-mode-option-content'>
                {React.cloneElement(icon, { className: 'design-mode-icon' })}
                <div className='design-mode-option-text'>
                    <p className='design-mode-label'>{label}</p>
                    <span className='design-mode-description'>{description}</span>
                </div>
            </div>
        </components.Option>
    );
};

const MenuList = (props) => {
    return (
        <components.MenuList className="design-mode-menu-list" {...props}>
            {props.children}
        </components.MenuList>
    );
};

const SelectedValue = (props) => {
    const { children } = props
    return (
        <components.SingleValue {...props} className={classNames('SingleValue', props.className)}>
            {children}
        </components.SingleValue>
    )
}

const DesignModeSelector = () => {
    const {
        state: { history, designMode, selectedSide, productCreatedType, pickerAttributes, designs },
        dispatch,
    } = useSelectedVariantContext()
    const {fetchCapturedSides} = usePreviewArtworks()
    const { sides } = useEditorSides()
    const isMockupTypeV2OrV3 = productCreatedType === 'preview3D' || productCreatedType === ''
    const [beforeSideChangeDesignMode, setBeforeSideChangeDesignMode] = useState(null)
    const options = useMemo(() => {
        const combinedEnabled = !!(
            (isMockupTypeV2OrV3 && sides?.includes('all-in-one'))
            || ['combineLayout', 'regularHasTemplate', 'regularNoTemplate'].includes(productCreatedType)
        );

        const splitDisabled = ['combineLayout', 'regularHasTemplate', 'regularNoTemplate'].includes(productCreatedType);
        return [
            {
                value: DESIGN_MODES.COMBINED,
                label: 'Combined layout',
                description: 'Supports uploading a completed and print-ready file to generate mockups and create different products simultaneously',
                icon: <CombinedLayoutIcon />,
                disabled: !combinedEnabled
            },
            {
                value: DESIGN_MODES.SPLIT,
                label: 'Split layout',
                description: 'Supports uploading multiple design layers and customization of specific placements to generate mockups and create one product at a time',
                icon: <SplitLayoutIcon />,
                disabled: splitDisabled
            }
        ]
    }, [productCreatedType, sides]);

    const optionDisabled = options.find(option => option.disabled)
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const toggle = () => setTooltipOpen(!tooltipOpen);

    const selectedOption = useMemo(() =>
        options.find(option => option.value === designMode) || options.filter(option => !option.disabled)[0],
        [designMode, options]
    );

    const handleChange = (selectedOption) => {
        if (!isMockupTypeV2OrV3) {
            return
        }
        if (selectedOption && selectedOption.value !== designMode) {
            setBeforeSideChangeDesignMode(selectedSide)
            dispatch(actionChangeDesignMode(selectedOption.value));
            if (selectedOption.value === DESIGN_MODES.COMBINED) {
                const fetchedDesigns = mergeAttributesByDesignEdited(pickerAttributes, designs)
                dispatch(updateActiveSide('all-in-one'))
                dispatch(actionChangeTabDesign(''))
                fetchCapturedSides(fetchedDesigns, false)
                return
            }
            const lastSide = history.logs.findLast(e => e.selectedSide !== 'all-in-one')?.selectedSide
            const fetchedDesigns = mergeAttributesByDesignEdited(pickerAttributes, designs)
            dispatch(updateActiveSide(beforeSideChangeDesignMode || lastSide))
            dispatch(actionChangeTabDesign(''))
            fetchCapturedSides(fetchedDesigns, false)
            return
        }
    };

    return (
        <>
            <div
                id="design-mode-selector-wrapper"
                className={`design-mode-selector me-3 ${productCreatedType === 'type2D' ? 'hidden' : ''}`}
            >
                <Select
                    components={{ Option: CustomOption, SingleValue: SelectedValue, MenuList }}
                    className="design-mode-dropdown"
                    value={selectedOption}
                    onChange={handleChange}
                    options={options}
                    isDisabled={!!optionDisabled}
                    isSearchable={false}
                />
            </div>
            {optionDisabled && <Tooltip
                placement="bottom"
                isOpen={tooltipOpen}
                target="design-mode-selector-wrapper"
                toggle={toggle}
                fade={false}
            >
                {`This product is not supported ${optionDisabled.label}`}
            </Tooltip>}
        </>
    )
}

export default DesignModeSelector
